import { CreateBucketCommand, GetObjectCommand, ListBucketsCommand, ListObjectsV2Command, S3Client } from "@aws-sdk/client-s3"
import { cloneDeep, first, invert, isNil, last, omit, omitBy, remove, uniqBy } from "lodash"
import { defineStore } from "pinia"
import { toRaw } from "vue"
import {
	BULK_SEARCH_COLORS,
	BULK_SEARCH_HEADER_CANDIDATES,
	FABActionType,
	FAB_SELECTED_QUICK_FORMAT_ROWS_STORAGE_KEY,
	IConfirmAttachmentResponse,
	IDeleteAttachmentResponse,
	INewUser,
	IOrder,
	IOrderQuickFormatRowSnapshot,
	IOrderRow,
	IRegisterUploadAttachmentResponse,
	ProspectSearchCriteria,
	ProspectSearchFields,
	PROSPECT_SEARCH_RESULTS_GRID_COLUMNS,
} from "@/lib/InterfacesAndTypes"
import { parseApiCalendarDate } from "@/lib/HelperDate"
import { applySearchExclusionRules, findIndexOfFirstIncludedValue, isStringDictionaryEmptyOrWhitespace, setPageTitle } from "@/lib/HelperFns"
import axios, { AxiosError } from "axios"
import { VueRouterInst } from "@/InitRoutesPlugins"
import { saveAs } from "file-saver"
import { DateTime } from "luxon"

export const Validators = {
	isEmailAddress(email: string): boolean {
		const regex = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/i
		return regex.test(email)
	},
}

const JSON_REQ_HEADERS = {
	"Content-Type": "application/json",
}

const ORDER_MERGE_OMIT_KEYS = [
	"orderRows",
	"attachments",
	"attachmentIds",
	"createdAtDT",
	"createdAtPretty",
	"closedAtDT",
	"closedAtPretty",
] as const

const ROW_MERGE_OMIT_KEYS = [
	"courtName",
	"dateOfBirthDT",
	"dateOfBirthPretty",
	"createdAtDT",
	"createdAtPretty",
	"closedAtDT",
	"closedAtPretty",
	"isSaving",
	"attachmentIsUploading",
] as const

type IDigitalOceanSpaces = {
	apiUrl: string
	baseUrl: string
	bucket: string
	accessKey: string
	secretKey: string
}

function getValidLocalStorageKey(key: string): any {
	const value = localStorage.getItem(key)
	if (value === null || value === "") {
		return null
	}
	return value
}

const QUICK_FORMAT_ROW_DT_KEYS = ["dateOfBirthDT", "createdAtDT", "closedAtDT"] as const

export function isOrderFabRoute(path: string): boolean {
	return path === "/orders" || path.startsWith("/order-detail/")
}

function parseStoredQuickFormatRows(): IOrderQuickFormatRowSnapshot[] {
	const raw = getValidLocalStorageKey(FAB_SELECTED_QUICK_FORMAT_ROWS_STORAGE_KEY)
	if (!raw) {
		return []
	}
	try {
		const parsed = JSON.parse(raw)
		return Array.isArray(parsed) ? parsed : []
	} catch {
		return []
	}
}

function snapshotOrderRowForQuickFormat(row: IOrderRow): IOrderQuickFormatRowSnapshot {
	const source = toRaw(row) as IOrderRow
	const plain = cloneDeep(source) as unknown as Record<string, unknown>
	for (const key of QUICK_FORMAT_ROW_DT_KEYS) {
		const v = plain[key]
		// de-Luxon the date values
		if (v != null && typeof (v as { toISO?: () => string }).toISO === "function") {
			plain[key] = (v as { toISO: () => string }).toISO()
		} else {
			delete plain[key]
		}
	}
	plain.orderId = source.orderId
	return {
		orderId: source.orderId,
		rowId: source.rowId,
		row: plain,
	}
}

function persistQuickFormatRows(rows: IOrderQuickFormatRowSnapshot[]) {
	localStorage.setItem(FAB_SELECTED_QUICK_FORMAT_ROWS_STORAGE_KEY, JSON.stringify(rows, null, 2))
}

export const useStore = defineStore('main', {
	state: () => {
		const fabQuickFormatRows = parseStoredQuickFormatRows()
		return {
			searchesActive: [] as ProspectSearchCriteria[],
			bulkProspectSearchCriteria: [] as ProspectSearchCriteria[],
			bulkProspectSearchProgress: 0,
			userAccounts: [],
			dataAuditRecords: [],

			FABSelectedReportRows: JSON.parse(getValidLocalStorageKey("FABSelectedReportRows") ?? "[]"),
			FABSelectedReportRowCount: 0,
			FABActionType: getValidLocalStorageKey("FABActionType") ?? null as FABActionType,
			FABShowCancel: false,

			currentUser: {
				emailAddress: '',
				password: '',
				name: '',
				isLoggingIn: false,
			},

			lookups: {
				countyByNumber: {},
				countyByName: {},
			},

			prospectSearchResultsGridColumns: PROSPECT_SEARCH_RESULTS_GRID_COLUMNS,

			orderMap: {} as Record<number, IOrder>,

			orderUILayout: 'expandable-detail' as 'detail' | 'expandable-detail',

			currentlySelectedOrderId: null as number | null,

			s3Client: null as S3Client | null,

			config: {
				digitalOceanSpaces: null as IDigitalOceanSpaces | null,
			},

			FABSelectedQuickFormatRows: fabQuickFormatRows,
			FABSelectedQuickFormatRowCount: fabQuickFormatRows.length,

			quickFormatModalActive: false,
			quickFormatMarkAllSentLoading: false,
		}
	},
	getters: {
		mostRecentSearch: (state) => {
			return last(state.searchesActive)
		},

		queuedBulkProspectSearches: (state) => {
			return state.bulkProspectSearchCriteria
		},

		isLoggedIn: (state) => {
			return state.currentUser.name || false
		},

		orders: (state) => {
			return Object.values(state.orderMap)
		},

		currentlySelectedOrder: (state) => {
			return state.currentlySelectedOrderId ? state.orderMap[state.currentlySelectedOrderId] : null
		},

		FABSelectedReportRowsAsSearchGrid: (state) => {
			return {
				results: state.FABSelectedReportRows,
			}
		},
	},
	actions: {
		setOrderUILayout(mode: 'detail' | 'expandable-detail') {
			this.orderUILayout = mode
		},

		selectFirstOrder() {
			if (this.orders.length === 0) {
				return null
			}
			this.currentlySelectedOrderId = this.orders[0].orderId
		},

		selectOrderByName(orderName: string) {
			this.currentlySelectedOrderId = this.orders.find((order) => order.orderName === orderName)?.orderId ?? null
			if (!this.currentlySelectedOrderId) {
				throw new Error(`Order ${orderName} not found`)
			}
			return this.currentlySelectedOrderId
		},

		selectOrderById(orderId: number) {
			this.currentlySelectedOrderId = orderId
			if (!(orderId in this.orderMap)) {
				throw new Error(`Order #${orderId} not found`)
			}
			return this.currentlySelectedOrderId
		},

		async fetchOrderList() {
			const url = '/api/order-list'
			const result = await axios.get(url)
			console.log({result})
			if (result.status === 200) {
				const orders = result.data as IOrder[]
				console.log({orders})
				for (const order of orders) {
					this.integrateFetchedOrder(order)
					console.log(order)
				}
				return this.orderMap
			}
			throw new Error('Failed to fetch order list')
		},

		async fetchOrderById(orderId: number): Promise<IOrder | null> {
			const url = `/api/order?orderId=${orderId}`
			const result = await axios.get(url)
			if (result.status === 200) {
				const orders = result.data as IOrder[]
				const order = first(orders)
				if (!order) {
					throw new Error(`Order #${orderId} not found`)
				}
				return this.integrateFetchedOrder(order)
			}
			return null
		},

		async fetchOrdersByName(orderName: string): Promise<IOrder[]> {
			const url = `/api/order?orderName=${orderName}`
			const result = await axios.get(url)
			if (result.status === 200) {
				const orders = result.data as IOrder[]
				return orders.map((order) => this.integrateFetchedOrder(order))
			}
			return []
		},

		async updateOrderRowCourtsSent(orderId: number, rowId: number, courtsSent: boolean) {
			return await this.updateOrderRowProps(orderId, rowId, { courtsSent })
		},

		async updateOrderRowCourtsReceived(orderId: number, rowId: number, courtsReceived: boolean) {
			return await this.updateOrderRowProps(orderId, rowId, { courtsReceived })
		},

		async updateOrderRowHitStatus(orderId: number, rowId: number, hitStatus: string) {
			return await this.updateOrderRowProps(orderId, rowId, { hitStatus })
		},

		async updateOrderRowInternalHitStatus(orderId: number, rowId: number, internalHitStatus: string) {
			return await this.updateOrderRowProps(orderId, rowId, { internalHitStatus })
		},

		async updateOrderRowNotes(orderId: number, rowId: number, notes: string) {
			return await this.updateOrderRowProps(orderId, rowId, { notes })
		},

		async updateOrderRowIsClosed(orderId: number, rowId: number, isClosed: boolean) {
			return await this.updateOrderRowProps(orderId, rowId, { isClosed })
		},

		async updateOrderRowSpecialFlags(orderId: number, rowId: number, specialFlags: string) {
			return await this.updateOrderRowProps(orderId, rowId, { specialFlags })
		},

		async updateOrderRowProps(orderId: number, rowId: number, props: any) {
			const url = `/api/order-row-props`
			const body = {
				orderId,
				rowId,
				...props,
			}
			const result = await axios.post(url, body)
			if (result.status === 200) {
				const data = result.data
				const resolvedOrderId = data.orderId ?? orderId
				const order = this.orderMap[resolvedOrderId]
				if (order) {
					const resolvedRowId = Number(data.rowId ?? rowId)
					const row = order.orderRows.find((r) => Number(r.rowId) === resolvedRowId)
					if (row) {
						if (data.courtsSent !== undefined) {
							row.courtsSent = data.courtsSent
						}
						if (data.courtsReceived !== undefined) {
							row.courtsReceived = data.courtsReceived
						}
						if (data.hitStatus !== undefined) {
							row.hitStatus = data.hitStatus
						}
						if (data.internalHitStatus !== undefined) {
							row.internalHitStatus = data.internalHitStatus
						}
						if (data.notes !== undefined) {
							row.notes = data.notes
						}
						if (data.isClosed !== undefined) {
							row.isClosed = data.isClosed
						}
						if (data.closedAt !== undefined) {
							row.closedAt = data.closedAt
						}
						if (data.specialFlags !== undefined) {
							row.specialFlags = data.specialFlags
						}
						row.isSaving = false
						console.log(`updateOrderRowProps ${orderId} row ${rowId}, setting isSaving to false`, row)
					}
				}
				return result.data
			}
			throw new Error(`Failed to update order row props (order #${orderId} row #${rowId}): ${result.data.errorMessage}`)
		},

		integrateFetchedOrder(fetched: IOrder): IOrder {
			if (!(fetched.orderId in this.orderMap)) {
				this.annotateOrdersData([fetched])
				this.orderMap[fetched.orderId] = fetched
				return fetched
			}
			const existing = this.orderMap[fetched.orderId]
			const rowUiState = new Map<number, { attachmentIsUploading: boolean }>()
			existing.orderRows = existing.orderRows ?? []
			for (const row of existing.orderRows) {
				rowUiState.set(row.rowId, {
					attachmentIsUploading: row.attachmentIsUploading ?? false,
				})
			}
			Object.assign(existing, omit(fetched, [...ORDER_MERGE_OMIT_KEYS]))
			existing.attachments = fetched.attachments ?? []
			existing.attachmentIds = fetched.attachmentIds ?? []
			const fetchedRows = fetched.orderRows ?? []
			const fetchedByRowId = new Map<number, IOrderRow>(
				fetchedRows.map((r) => [r.rowId, r]),
			)
			const existingRowIds = new Set(existing.orderRows.map((r) => r.rowId))
			for (const existingRow of existing.orderRows) {
				const fr = fetchedByRowId.get(existingRow.rowId)
				if (fr) {
					Object.assign(existingRow, omit(fr, [...ROW_MERGE_OMIT_KEYS]))
				}
			}
			for (const fr of fetchedRows) {
				if (!existingRowIds.has(fr.rowId)) {
					existing.orderRows.push(fr)
					existingRowIds.add(fr.rowId)
				}
			}
			this.annotateOrdersData([existing])
			for (const row of existing.orderRows) {
				const ui = rowUiState.get(row.rowId)
				if (ui) {
					row.attachmentIsUploading = ui.attachmentIsUploading
				}
			}
			return existing
		},

		//TODO: This function is ghetto and I'd like to ditch it, but we can't turn isSaving into an observable unless it's defined on the databound object, so it needs to come from somewhere and the server isn't the right choice.
		annotateOrdersData(orders: IOrder[]) {
			for (const order of orders) {
				order.orderRows = order.orderRows ?? []
				order.attachments = order.attachments ?? []
				order.attachmentIds = order.attachmentIds ?? []
				order.attachments.forEach((attachment) => {
					attachment.dropdownActive = attachment.dropdownActive ?? false
				})
				order.createdAtDT = parseApiCalendarDate(order.createdAt)
				order.createdAtPretty = order.createdAtDT?.toFormat('yyyy-MM-dd') ?? ""
				order.closedAtDT = parseApiCalendarDate(order.closedAt)
				order.closedAtPretty = order.closedAtDT?.toFormat('yyyy-MM-dd') ?? ""
				order.orderRows.forEach((row) => {
					row.courtName = this.lookups.countyByNumber[row.courtCode]
					row.isSaving = false
					row.specialFlags = row.specialFlags ?? ''
					row.dateOfBirthDT = parseApiCalendarDate(row.dateOfBirth)
					row.dateOfBirthPretty = row.dateOfBirthDT?.toFormat('yyyy-MM-dd') ?? ""
					row.createdAtDT = parseApiCalendarDate(row.createdAt)
					row.createdAtPretty = row.createdAtDT?.toFormat('yyyy-MM-dd') ?? ""
					row.closedAtDT = parseApiCalendarDate(row.closedAt)
					row.closedAtPretty = row.closedAtDT?.toFormat('yyyy-MM-dd') ?? ""
					row.attachments = row.attachments ?? []
					row.attachmentIds = row.attachmentIds ?? []
					row.attachmentIsUploading = row.attachmentIsUploading ?? false
					row.attachments.forEach((attachment) => {
						attachment.dropdownActive = attachment.dropdownActive ?? false
					})
					// console.log(`annotateOrdersData ${order.orderId} row ${row.rowId}`, row)
				})
			}
			return orders
		},

		async setOrderIsClosed(orderId: number, isClosed: boolean) {
			const url = `/api/order-update-is-closed`
			const body = {
				orderId,
				isClosed,
			}
			const result = await axios.post(url, body)
			if (result.status === 200) {
				return result.data
			}
		},

		async deleteOrder(orderId: number) {
			const url = `/api/order-delete`
			const body = {
				orderId,
			}
			const result = await axios.post(url, body)
			if (result.status === 200) {
				return result.data
			}
		},

		async putPresignedAttachmentUpload(uploadUrl: string, file: File, headers: Record<string, string>) {
			const presignedUploadErrorBodyMaxChars = 500;
			let res: Response;
			try {
				res = await fetch(uploadUrl, {
					method: "PUT",
					body: file,
					headers,
				});
			} catch (err) {
				const msg = err instanceof Error ? err.message : String(err);
				throw new Error(
					`Attachment upload to storage failed (network). Check the Network tab for the PUT to Spaces; a CORS console message often hides 403 signature errors. ${msg}`,
				);
			}
			if (!res.ok) {
				const detail = (await res.text()).slice(0, presignedUploadErrorBodyMaxChars);
				const suffix = detail.length > 0 ? `: ${detail}` : "";
				throw new Error(`Attachment upload to storage failed (${res.status})${suffix}`);
			}
		},

		async postOrderRowUploadAttachment(params: {
			orderId: number,
			rowId: number,
			filename: string,
			contentType: string,
			sizeBytes: number,
		}): Promise<IRegisterUploadAttachmentResponse> {
			const url = "/api/order-row-upload-attachment"
			const result = await axios.post(url, params, { headers: JSON_REQ_HEADERS })
			if (result.status === 200) {
				return result.data as IRegisterUploadAttachmentResponse
			}
			throw new Error((result.data as any)?.errorMessage ?? "order-row-upload-attachment failed")
		},

		async postOrderUploadAttachment(params: {
			orderId: number,
			filename: string,
			contentType: string,
			sizeBytes: number,
		}): Promise<IRegisterUploadAttachmentResponse> {
			const url = "/api/order-upload-attachment"
			const result = await axios.post(url, params, { headers: JSON_REQ_HEADERS })
			if (result.status === 200) {
				return result.data as IRegisterUploadAttachmentResponse
			}
			throw new Error((result.data as any)?.errorMessage ?? "order-upload-attachment failed")
		},

		async postConfirmAttachment(params: { attachmentId: string }): Promise<IConfirmAttachmentResponse> {
			const url = "/api/confirm-attachment"
			const result = await axios.post(url, params, { headers: JSON_REQ_HEADERS })
			if (result.status === 200) {
				return result.data as IConfirmAttachmentResponse
			}
			throw new Error((result.data as any)?.errorMessage ?? "confirm-attachment failed")
		},

		async deleteOrderRowAttachment(params: {
			orderId: number,
			rowId: number,
			attachmentId: string,
		}): Promise<IDeleteAttachmentResponse> {
			const url = "/api/order-row-delete-attachment"
			const result = await axios.delete(url, {
				data: params,
				headers: JSON_REQ_HEADERS,
			})
			if (result.status === 200) {
				return result.data as IDeleteAttachmentResponse
			}
			throw new Error((result.data as any)?.errorMessage ?? "order-row-delete-attachment failed")
		},

		async deleteOrderAttachment(params: {
			orderId: number,
			attachmentId: string,
		}): Promise<IDeleteAttachmentResponse> {
			const url = "/api/order-delete-attachment"
			const result = await axios.delete(url, {
				data: params,
				headers: JSON_REQ_HEADERS,
			})
			if (result.status === 200) {
				return result.data as IDeleteAttachmentResponse
			}
			throw new Error((result.data as any)?.errorMessage ?? "order-delete-attachment failed")
		},

		async uploadOrderAttachmentFile(orderId: number, file: File) {
			const reg = await this.postOrderUploadAttachment({
				orderId,
				filename: file.name,
				contentType: file.type || "application/octet-stream",
				sizeBytes: file.size,
			})
			await this.putPresignedAttachmentUpload(reg.uploadUrl, file, reg.headers)
			await this.postConfirmAttachment({ attachmentId: reg.attachmentId })
			await this.fetchOrderById(orderId)
		},

		async deleteOrderAttachmentAndRefresh(orderId: number, attachmentId: string) {
			await this.deleteOrderAttachment({ orderId, attachmentId })
			await this.fetchOrderById(orderId)
		},

		validateIsEmailAddress(email: string) {
			return Validators.isEmailAddress(email)
		},

		addEmptySearch() {
			this.searchesActive.push(new ProspectSearchCriteria())
			setPageTitle(`${this.searchesActive.length} Prospect Searches`)
			this.updateSearchTitle()
		},

		addSearch(search: ProspectSearchCriteria) {
			this.searchesActive.push(search)
			this.updateSearchTitle()
		},

		closeSearch(search: ProspectSearchCriteria) {
			this.searchesActive.remove(search)
			this.updateSearchTitle()
		},

		duplicateSearch(search: ProspectSearchCriteria) {
			const duplicate = new ProspectSearchCriteria()
			duplicate.fields = { ...search.fields }
			const index = this.searchesActive.indexOf(search)
			if (index === -1) {
				this.searchesActive.push(duplicate)
			} else {
				this.searchesActive.splice(index + 1, 0, duplicate)
			}
			this.updateSearchTitle()
			this.submitSearch(duplicate)
		},

		closeSearchByIndex(i: number) {
			this.searchesActive.splice(i, 1)
			this.updateSearchTitle()
		},

		updateSearchTitle() {
			setPageTitle(`${this.searchesActive.length} Prospect Searches`)
		},

		setFABAction(actionType: FABActionType) {
			this.FABActionType = actionType
			if (actionType === "generate-report") {
				this.FABSelectedReportRows = []
			}
			if (actionType === null) {
				this.FABSelectedReportRows = []
				this.FABShowCancel = false
			}
			localStorage.setItem("FABActionType", actionType as string)
			localStorage.setItem("FABSelectedReportRows", JSON.stringify(this.FABSelectedReportRows, null, 2))
		},

		setFABShowCancel(show: boolean) {
			this.FABShowCancel = show
		},

		addReportRow(row: any, rowSearchContext: ProspectSearchFields) {
			if (this.FABActionType !== "generate-report") {
				this.setFABAction("generate-report")
			}

			const combinedRows: any[] = []
			if (this.FABSelectedReportRows?.length) {
				combinedRows.push(...this.FABSelectedReportRows)
			}
			row.__searchContext = Object.assign({}, rowSearchContext)
			combinedRows.push(row)
			// console.log({row})
			const uniqueRows = uniqBy(combinedRows, 'DOCKET')
			// console.log('addReportRow', uniqueRows)
			if (uniqueRows.length === 0) {
				return this.clearFABAction()
			}
			this.FABSelectedReportRows = uniqueRows
			this.FABSelectedReportRowCount = uniqueRows.length
			localStorage.setItem("FABSelectedReportRows", JSON.stringify(this.FABSelectedReportRows, null, 2))
		},

		removeReportRow(row: any) {
			if (this.FABActionType !== "generate-report") {
				this.setFABAction("generate-report")
			}

			remove(this.FABSelectedReportRows, (value: any) => {
				// console.log(`value.DOCKET (${value.DOCKET}) === row.DOCKET (${row.DOCKET})`, {
				// 	value, row,
				// })
				return value.DOCKET === row.DOCKET && value.ACTION_SEQ === row.ACTION_SEQ
			})
			this.FABSelectedReportRowCount = this.FABSelectedReportRows.length
			// console.log('removeReportRow', this.FABSelectedReportRows, this.FABSelectedReportRows.length, this.FABSelectedReportRowCount)
			localStorage.setItem("FABSelectedReportRows", JSON.stringify(this.FABSelectedReportRows, null, 2))
		},

		addQuickFormatOrderRow(row: IOrderRow) {
			const snap = snapshotOrderRowForQuickFormat(row)
			const combinedRows: IOrderQuickFormatRowSnapshot[] = []
			if (this.FABSelectedQuickFormatRows?.length) {
				combinedRows.push(...this.FABSelectedQuickFormatRows)
			}
			combinedRows.push(snap)
			const uniqueRows = uniqBy(combinedRows, (r) => `${r.orderId}-${r.rowId}`)
			if (uniqueRows.length === 0) {
				this.FABSelectedQuickFormatRows = []
				this.FABSelectedQuickFormatRowCount = 0
				localStorage.removeItem(FAB_SELECTED_QUICK_FORMAT_ROWS_STORAGE_KEY)
				return
			}
			this.FABSelectedQuickFormatRows = uniqueRows
			this.FABSelectedQuickFormatRowCount = uniqueRows.length
			persistQuickFormatRows(uniqueRows)
		},

		removeQuickFormatOrderRow(row: IOrderRow) {
			remove(this.FABSelectedQuickFormatRows, (value: IOrderQuickFormatRowSnapshot) => {
				return value.orderId === row.orderId && value.rowId === row.rowId
			})
			this.FABSelectedQuickFormatRowCount = this.FABSelectedQuickFormatRows.length
			if (this.FABSelectedQuickFormatRows.length === 0) {
				localStorage.removeItem(FAB_SELECTED_QUICK_FORMAT_ROWS_STORAGE_KEY)
			} else {
				persistQuickFormatRows(this.FABSelectedQuickFormatRows)
			}
		},

		syncQuickFormatOrderRowsFromCheckedList(orderId: number, checkedRowsForOrder: IOrderRow[]) {
			const others = this.FABSelectedQuickFormatRows.filter((s) => s.orderId !== orderId)
			const newSnaps = checkedRowsForOrder.map((r) => snapshotOrderRowForQuickFormat(r))
			const merged = [...others, ...newSnaps]
			this.FABSelectedQuickFormatRows = merged
			this.FABSelectedQuickFormatRowCount = merged.length
			if (merged.length === 0) {
				localStorage.removeItem(FAB_SELECTED_QUICK_FORMAT_ROWS_STORAGE_KEY)
			} else {
				persistQuickFormatRows(merged)
			}
		},

		async doFABAction() {
			if (isOrderFabRoute(VueRouterInst.currentRoute.path) && this.FABSelectedQuickFormatRows.length > 0) {
				this.quickFormatModalActive = true
				return
			}
			if (this.FABActionType === "generate-report") {
				if (VueRouterInst.currentRoute.path === "/review-report") {
					await this.generateAndDownloadReport()
				} else {
					VueRouterInst.push({ path: '/review-report' })
				}
			}
		},

		async generateAndDownloadReport() {
			console.group('generateAndDownloadReport')
			const url = '/api/generate-report'
			const body = {
				selectedReportRows: this.FABSelectedReportRows,
			}
			console.log({body})
			try {
				const result = await axios.post(url, body, {
					responseType: 'blob',
					headers: {
						'Accept': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
					},
					insecureHTTPParser: true,
				})
				const blob = new Blob([result.data], {
					type: result.headers['Content-Type'],
				})
				console.log(result.headers)
				const link = document.createElement('a')
				link.href = window.URL.createObjectURL(blob)
				link.setAttribute('download', result.headers['x-wm-filename'] ?? 'REPORT.docx')
				link.click()
				console.groupEnd()
				return result
			} catch (err) {
				console.error('Generate report failed', err)
				console.groupEnd()
			}

		},

		async uploadOrder(file: File) {
			console.group('uploadOrder')
			const url = '/api/order-upload'
			const formData = new FormData()
			formData.append('file', file)
			console.log({filename: file.name})
			try {
				const result = await axios.post(url, formData, {
					// headers: {
					// 	'Content-Type': 'multipart/form-data',
					// },
				})
				console.log('Order uploaded successfully', result.data)
				console.groupEnd()
				return result.data
			} catch (err) {
				console.error('Upload order failed', err)
				console.groupEnd()
				throw err
			}
		},

		closeQuickFormatModal() {
			this.quickFormatModalActive = false
		},

		async markAllQuickFormatRowsSentThenClearFab() {
			if (this.FABSelectedQuickFormatRows.length === 0) {
				this.clearAllFabPersistedState()
				return
			}
			const snapshots = [...this.FABSelectedQuickFormatRows]
			this.quickFormatMarkAllSentLoading = true
			try {
				for (const s of snapshots) {
					await this.updateOrderRowCourtsSent(s.orderId, s.rowId, true)
				}
				const orderIds = [...new Set(snapshots.map((row) => row.orderId))]
				for (const orderId of orderIds) {
					await this.fetchOrderById(orderId)
				}
				this.clearAllFabPersistedState()
			} finally {
				this.quickFormatMarkAllSentLoading = false
			}
		},

		clearAllFabPersistedState() {
			// TODO: set court sent note for later
			this.quickFormatModalActive = false
			this.FABSelectedQuickFormatRows = []
			this.FABSelectedQuickFormatRowCount = 0
			localStorage.removeItem(FAB_SELECTED_QUICK_FORMAT_ROWS_STORAGE_KEY)
			this.FABActionType = null
			this.FABSelectedReportRows = []
			this.FABSelectedReportRowCount = 0
			this.FABShowCancel = false
			localStorage.removeItem("FABActionType")
			localStorage.removeItem("FABSelectedReportRows")
		},

		clearFABAction() {
			if (isOrderFabRoute(VueRouterInst.currentRoute.path)) {
				this.FABSelectedQuickFormatRows = []
				this.FABSelectedQuickFormatRowCount = 0
				localStorage.removeItem(FAB_SELECTED_QUICK_FORMAT_ROWS_STORAGE_KEY)
				return
			}
			this.FABActionType = null
			this.FABSelectedReportRows = []
			this.FABSelectedReportRowCount = 0
			this.FABShowCancel = false
			localStorage.removeItem("FABActionType")
			localStorage.removeItem("FABSelectedReportRows")
			if (VueRouterInst.currentRoute.path === "/review-report") {
				VueRouterInst.push({ path: '/search' })
			}
		},

		downloadCSVBulkTemplate() {
			console.log(`downloadCSVTemplate()`)
			const csvHeaderRow = 'First Name,Middle Name,Last Name,Date of Birth,Court Code,Location Code,Division,Case ID'
			const csvFile = new File([csvHeaderRow], 'BulkProspectSearchCriteria.csv', { type: 'text/csv;charset=utf-8' });
			saveAs(csvFile)
		},

		parseCSVBulkFile(rawText: string, allowCommas = false) {
			console.group('parseCSVBulkFile')
			console.log(`parseCSVBulkFile()`, rawText)
			this.bulkProspectSearchCriteria = []

			if (!rawText) {
				// TODO (KJE, 6/1/2023): Better UX when doing UX for Bulk Search Page
				console.log('received null or undefined input to parseCSVBulkFile() method. What went wrong?')
				return
			}

			const lines = rawText.split('\r\n')

			if (!lines || lines.length === 0) {
				// TODO (KJE, 6/1/2023): Better UX when doing UX for Bulk Search Page
				console.log('received blank CSV file in parseCSVBulkFile().  Clearing criteria.')
				return

			}


			const firstLine = lines.first()
			const firstLineValues = firstLine.split(',')
			for (let count = 0; count < firstLineValues.length; count++) {
				firstLineValues[count] = firstLineValues[count].toLowerCase()
			}

			const firstNameIndex = findIndexOfFirstIncludedValue(firstLineValues, BULK_SEARCH_HEADER_CANDIDATES.firstName)
			const lastNameIndex = findIndexOfFirstIncludedValue(firstLineValues, BULK_SEARCH_HEADER_CANDIDATES.lastName)
			const middleNameIndex = findIndexOfFirstIncludedValue(firstLineValues, BULK_SEARCH_HEADER_CANDIDATES.middleName)
			const dobIndex = findIndexOfFirstIncludedValue(firstLineValues, BULK_SEARCH_HEADER_CANDIDATES.dateOfBirth)
			const courtCodeIndex = findIndexOfFirstIncludedValue(firstLineValues, BULK_SEARCH_HEADER_CANDIDATES.courtCode)
			const locationCodeIndex = findIndexOfFirstIncludedValue(firstLineValues, BULK_SEARCH_HEADER_CANDIDATES.locationCode)
			const divisionIndex = findIndexOfFirstIncludedValue(firstLineValues, BULK_SEARCH_HEADER_CANDIDATES.division)
			const caseIdIndex = findIndexOfFirstIncludedValue(firstLineValues, BULK_SEARCH_HEADER_CANDIDATES.caseId)

			// Remove header row
			lines.splice(0, 1)

			if (lines.length === 0) {
				// TODO (KJE, 6/1/2023): Better UX when doing UX for Bulk Search Page
				console.log('received blank CSV file in parseCSVBulkFile().  Clearing criteria.')
				return
			}

			lines.forEach((line: string) => {
				// NOTE: this assumes CSV files will place any cell data containing a comma inside of ""s
				//       Unless the CSV is built erroneously by hand, this really SHOULD be true.

				// Deal with cells ending in ", AR" by removing the ", AR"
				if (line.match(/, *AR/)) {
					line = line.replace(/\"(.+,) *AR *\"/, "\"$1\"")
				}

				const escapedLine = this.escapeEmbeddedCommasInCSV(line)
				const incFields = escapedLine.split(',') as string[]
				const unescapedFields: string[] = []

				incFields.forEach((field:string) => {
					if (!!field && allowCommas) {
						unescapedFields.push(field.replaceAll("&#44;", ","))
					} else if (!!field && !allowCommas) {
						unescapedFields.push(field.replaceAll("&#44;", ""))
					} else {
						unescapedFields.push("")
					}
				})

				const prospect = new ProspectSearchCriteria()
				prospect.fields = {
					firstName: firstNameIndex === -1 ? '' : unescapedFields[firstNameIndex],
					middleName: middleNameIndex === -1 ? '' : unescapedFields[middleNameIndex],
					lastName: lastNameIndex === -1 ? '' : unescapedFields[lastNameIndex],
					dateOfBirth: dobIndex === -1 ? '' : unescapedFields[dobIndex],
					dateOfBirthNullCheck: false,
					courtCode: courtCodeIndex === -1 ? '' : unescapedFields[courtCodeIndex],
					locationCode: locationCodeIndex === -1 ? '' : unescapedFields[locationCodeIndex],
					division: divisionIndex === -1 ? '' : unescapedFields[divisionIndex],
					caseId: caseIdIndex === -1 ? '' : unescapedFields[caseIdIndex],
				}
				if (isStringDictionaryEmptyOrWhitespace(prospect.fields)) {
					// TODO (KJE, 6/13/2023): Better UX when doing UX for Bulk Search Page
					console.log('Empty prospect found. Skipping prospect.', line)
				} else {
					this.bulkProspectSearchCriteria.push(prospect)
				}
			})
			setPageTitle(`${lines.length} Bulk Searches`)
			console.groupEnd()
		},

		escapeEmbeddedCommasInCSV(line: string) {
			// Deal with cells that include one or more commas in their data
			// 1) Add filler | on either side of string to avoid first/last cell shenanigans
			// 2) Split line by "
			// 	2a) NOTE: a sheet that has a cell containing a single " value will disrupt this logic for that line
			// 3) For each EVEN cell, replace the commas with HTML Escape (&#44;)
			// 4) Re-join cells
			// 5) Remove | filler text
			// 6) Replace HTML Escape with , when pushing cell to fields dictionary

			let escapedLine = line
			if (line.match(/"/)) {
				const lineForParsing = `|${line}|`
				const splitByQuotes = lineForParsing.split("\"")
				const escapedSplitByQuotes: string[] = []
				splitByQuotes.forEach((section:string, index:number) => {
					if (index % 2 === 0) {
						escapedSplitByQuotes.push(section)
					} else {
						escapedSplitByQuotes.push(section.replaceAll(",", "&#44;"))
					}
				})
				escapedLine = escapedSplitByQuotes.join("")
				escapedLine = escapedLine.substring(1, escapedLine.length - 1)
			}

			return escapedLine
		},

		async runEntireBulkProspectSearch() {
			console.groupCollapsed('runEntireBulkProspectSearch')

			//Number of threads allowed to search at once
			const increment = 10

			const setBulkColorFunction = ((index: number) => {
				const colorMe = this.bulkProspectSearchCriteria[index]
				if (colorMe.hasErrors) {
					colorMe.bulkSearchColor = BULK_SEARCH_COLORS['Error']
				} else if (colorMe.hasResults) {
					colorMe.bulkSearchColor = BULK_SEARCH_COLORS['Results']
				} else {
					colorMe.bulkSearchColor = BULK_SEARCH_COLORS['Empty']
				}
			})

			if (!this.bulkProspectSearchCriteria || !this.bulkProspectSearchCriteria.length) {
				console.error('No criteria to search against.')
				console.groupEnd()
			}

			for (let promisesKicked = 0; promisesKicked < increment; promisesKicked++) {
				if (this.bulkProspectSearchCriteria.length > promisesKicked) {
					this.runSingleSearchForBulkAndAttemptNext(promisesKicked, increment, setBulkColorFunction)
				} else {
					break
				}
			}

			console.groupEnd()
		},
		async runSingleSearchForBulkAndAttemptNext(index: number, increment: number, setBulkColorFunction: any) {
			console.log(`Running single search for Bulk, index ${index}`)

			await this.submitSearch(this.bulkProspectSearchCriteria[index], false)

			setBulkColorFunction(index)

			this.bulkProspectSearchProgress += 1

			if (this.bulkProspectSearchProgress < this.bulkProspectSearchCriteria.length) {
				index += increment
				if (this.bulkProspectSearchCriteria.length > index) {
					this.runSingleSearchForBulkAndAttemptNext(index, increment, setBulkColorFunction)
				}
			} else {
				this.initialSortBulkProspectSearchData()
			}
		},
		initialSortBulkProspectSearchData() {
			const columnSortCompareResults = (a, b) => {
				return a.results.length - b.results.length
			}

			this.bulkProspectSearchCriteria.sort(columnSortCompareResults)

			this.bulkProspectSearchCriteria.reverse()
		},

		async submitCreateNewUser(newUser: INewUser) {
			console.group('submitCreateNewUser')
			const url = '/api/user-create'
			const body = {
				name: newUser.name,
				emailAddress: newUser.emailAddress,
				password: newUser.password,
			}
			console.log({body})
			const result = await axios.post(url, body)
			if (result.status === 200) {
				await this.fetchUserAccounts()
				console.groupEnd()
				return true
			} else {
				console.error('Create new user failed', result)
				console.groupEnd()
				return false
			}
		},

		async changeUserPassword(emailAddress: string, newPassword: string) {
			console.group('changeUserPassword')
			const url = '/api/user-change-password'
			const body = {
				emailAddress: emailAddress,
				password: newPassword,
			}
			console.log({body})
			try {
				const result = await axios.post(url, body)
				console.groupEnd()
				return true
			} catch (err) {
				console.error('Change user password failed', err)
				console.groupEnd()
				return false
			}
		},

		async deleteUser(emailAddress: string) {
			console.group('deleteUser')
			const url = '/api/user-delete'
			const body = {
				emailAddress: emailAddress,
			}
			console.log({body})
			try {
				await axios.post(url, body)
				await this.fetchUserAccounts()
				console.groupEnd()
				return true
			} catch (err) {
				console.error('Delete user failed', err)
				console.groupEnd()
				return false
			}
		},

		async submitLogin() {
			console.group('Logging in...')
			this.currentUser.isLoggingIn = true
			const url = '/api/login'
			const body = {
				emailAddress: this.currentUser.emailAddress,
				password: this.currentUser.password,
			}
			try {
				const result = await axios.post(url, body)
				const user = result.data
				this.setCurrentUser(user)
				console.log(`Logged in as ${this.currentUser.name}`)
				this.currentUser.isLoggingIn = false
				console.groupEnd()
				return true
			} catch (err) {
				console.error('Login failed', err)
				this.currentUser.isLoggingIn = false
				console.groupEnd()
				return false
			}
		},

		async getLoggedInUserDetails() {
			const url = 'api/logged-in'
			const result = await axios.post(url)
			if (result.status === 200) {
				const user = result.data
				this.setCurrentUser(user)
				console.log(`Logged in user details:`, user)
			}
		},

		async getKeyValueRecords() {
			const url = 'api/key-value?keyId='
			const params = ['county_lookup', 'spaces_readonly']
			const promises = params.map((param) => {
				return axios.get(url + param)
			})
			const results = await Promise.all(promises)
			const [ countyLookup, spacesReadonly ] = results
			this.lookups.countyByNumber = countyLookup.data.value
			this.lookups.countyByName = invert(this.lookups.countyByNumber)
			for (const key in this.lookups.countyByName) {
				const value = this.lookups.countyByName[key]
				if (value.startsWith('0') && value.length > 2) {
					const trimmedValue = +value
					this.lookups.countyByName[key] = String(trimmedValue)
				}
			}

			//TODO: DEPRECATED? 2026/03/22
			if (spacesReadonly.data.value) {
				this.config.digitalOceanSpaces = spacesReadonly.data.value
				// this.config.digitalOceanSpaces!.apiUrl = "https://atl1.digitaloceanspaces.com"
				this.initializeS3Client()
			}
		},

		//TODO: DEPRECATED? 2026/03/22
		initializeS3Client() {
			if (!this.config.digitalOceanSpaces) {
				return
			}

			const spaces = this.config.digitalOceanSpaces

			this.s3Client = new S3Client({
				endpoint: spaces.apiUrl,
				region: 'us-east-1',
				credentials: {
					accessKeyId: spaces.accessKey,
					secretAccessKey: spaces.secretKey,
				},
				forcePathStyle: false,
			})
		},

		async fetchDataAuditRecords() {
			const url = 'api/audit-log?startDate=2023-03-01&endDate=2030-01-01&includeMetadata=true'
			const result = await axios.get(url)
			console.log({result})
			if (result.status === 200) {
				this.dataAuditRecords = result.data.map((record: any) => {
					return {
						...record,
						jobResults: `<pre>${JSON.stringify(record.jobResults, null, 2)}</pre>`,
						jobMetadata: JSON.stringify(record.jobMetadata, null, 2),
					}
				})
			}
		},

		async fetchUserAccounts() {
			const url = 'api/user-accounts'
			const result = await axios.get(url)
			console.log({result})
			if (result.status === 200) {
				this.userAccounts = result.data
			}
		},

		async submitLogout() {
			console.group(`Logging out of ${this.currentUser.name}`)
			this.clearCurrentUser()
			VueRouterInst.push({ path: '/login' })
			console.groupEnd()
		},

		setCurrentUser(user: any) {
			this.currentUser.name = user.name
			this.currentUser.emailAddress = user.emailAddress
			this.currentUser.password = ''
			localStorage.setItem('auth-token', user.authentication)
			axios.defaults.headers.common['auth-token'] = user.authentication
		},

		clearCurrentUser() {
			this.currentUser.emailAddress = ''
			this.currentUser.name = ''
			this.currentUser.password = ''
			localStorage.setItem('auth-token', '')
		},

		onSearchResultsTableCellClick(row, column, timeout, buefy) {
			const columnKey = column._props.field ?? column.newKey
			const now = Date.now()
			if (columnKey === 'DOCKET' || now <= timeout + 300) {
				const value = row[columnKey]
				navigator.clipboard.writeText(value)
				buefy.toast.open({
					duration: 3000,
					message: `Copied '${value}' to clipboard!`,
					type: 'is-success',
				})
			}
			return now
		},

		async submitSearch(search: ProspectSearchCriteria, logSearch: Boolean = true) {
			console.group(`Searching...`)
			const start = Date.now()
			search.fields.firstName = search.fields.firstName?.toUpperCase()
			search.fields.middleName = search.fields.middleName?.toUpperCase()
			search.fields.lastName = search.fields.lastName?.toUpperCase()
			search.fields.courtCode = search.fields.courtCode?.toUpperCase()
			console.groupCollapsed(`checking courtCode: ${search.fields.courtCode}`)
			if (search.fields.courtCode && !/[0-9]+/i.test(search.fields.courtCode)) {
				search.fields.courtCode = this.lookups.countyByName[search.fields.courtCode]
				console.log(`detected name, converting to number, now: ${search.fields.courtCode}`)
			}
			console.groupEnd()
			search.fields = omitBy(search.fields, isNil)
			for (const key in search.fields) {
				if (search.fields[key] == '') {
					delete search.fields[key]
				}
			}
			search.isSearching = true
			search.hasErrors = false
			if (logSearch) {
				console.log('Sanitized search: ', search)
			}
			// const params = new URLSearchParams(search.fields)
			const url = '/api/search-case-records?'
			if (logSearch) {
				console.log({url, fields: search.fields})
			}
			let data
			try {
				const results = await axios.get(url, {
					params: {
						...search.fields,
					},
				})
				data = results.data
			} catch (e) {
				const err = e as AxiosError
				console.error(err)
				search.isSearching = false
				search.hasErrors = {
					message: err.message,
					status: err.response?.status,
					statusText: err.response?.statusText,
				}
				return
			}
			data.forEach((record) => {
				console.log(record)
				record['AOC_LINK'] = `<a target="_blank" href="https://caseinfo.arcourts.gov/opad/case/${record.DOCKET}"><i class="mdi mdi-open-in-new mdi-18px"></i></a>`
			})
			console.log('Records returned:', data.length)
			const uniqueDocketKeys = [...new Set(data.map((item: any) => item.DOCKET))]
			const coalescedRecords:any[] = []		// Return value: most recent non-null, non-'' values, per field, per docket
			let index = 0

			uniqueDocketKeys.forEach((uniqueDocketKey: any) => {
				coalescedRecords.push({
					DOCKET: uniqueDocketKey,
				})

				const rowsForDocket: any[] = Object.values(
					data.filter((dataRow: any) => dataRow.DOCKET === uniqueDocketKey))

				// Sort the rows for the docket in descending order (most recent items first) by ACTION_SEQ
				rowsForDocket.sort((a: any, b: any) => {
					if (a.ACTION_SEQ < b.ACTION_SEQ) {
						return -1
					} else if (a.ACTION_SEQ > b.ACTION_SEQ) {
						return 1
					} else {
						return 0
					}
				})
				.reverse()

				coalescedRecords[index]['RAW_RECORDS'] = rowsForDocket

				// Grab the most recent non-null, non-'' value for each field for this Docket ID
				// Fill in any blanks with empty string
				Object.keys(rowsForDocket[0]).forEach((dataKey: any) => {
					const filteredRows: any[] = rowsForDocket.filter((row: any) => (row[dataKey] !== undefined && row[dataKey] !== null && row[dataKey] !== ''))

					if (filteredRows !== undefined && filteredRows !== null && filteredRows.length > 0) {
						coalescedRecords[index][dataKey] = filteredRows[0][dataKey]
					} else {
						coalescedRecords[index][dataKey] = ''
					}
				})


				rowsForDocket.forEach((row) => {
					applySearchExclusionRules(row, coalescedRecords[index], rowsForDocket)
				})

				index++
			})

			search.results = coalescedRecords
			search.rawResultCount = data.length
			search.isSearching = false
			search.hasSearched = true
			search.queryDuration = Date.now() - start
			console.groupEnd()
		},

		updateColumnPresetSelected(preset: string) {
			const visibleColumns: string[] = []

			const basic = ['COURT_CODE', 'COURT_LOCN', 'COURT_NAME', 'DOCKET', 'AOC_LINK', 'DIVISION', 'DEF_LNAME', 'DEF_FNAME', 'DEF_MI', 'DOB', 'AL_LNAME', 'AL_FNAME', 'AL_MI']

			if (preset === 'basic') {
				visibleColumns.push(...basic)
			} else if (preset === 'important') {
				visibleColumns.push(...basic)
				visibleColumns.push('CHARGE', 'CHARGE_DESC', 'CHARGE_CLASS', 'CHARGE_CLASS_DESC', 'CHARGE_NUM', 'FILE_DATE', 'PLEA', 'DISP', 'DISP_CHRG_NO', 'DISP_DATE', 'SENTENCE_CODE', 'SENTENCE_LENGTH', 'SENTENCE_UNIT', 'FINE', 'REST', 'CASE_STATUS')
			} else if (preset === 'everything') {
				visibleColumns.push(...this.prospectSearchResultsGridColumns.map((column) => column.field))
			}

			for (const column of this.prospectSearchResultsGridColumns) {
				column.visible = visibleColumns.includes(column.field)
			}
		},

	},
})
